import { editFlowOpenState, editingFlowState, liveGuideFlowState } from '@/state';
import { useUnwrappedAtomValue } from '@/state/loadableHooks';
import { type AppFlowObject, type AppFlowParams, DEFAULT_EQUIPMENT_SPACE_ID } from '@rock/types';
import { useSetAtom } from 'jotai';

/** Module-level so every caller shares one atomFamily entry — `shallowEqual` counts keys, so an
 *  inline `{ spaceId }` and `{ spaceId, equipmentTypeId: undefined }` are two different listeners. */
const DEFAULT_EQUIPMENT_FLOW_PARAMS: AppFlowParams = {
    spaceId: DEFAULT_EQUIPMENT_SPACE_ID,
    equipmentTypeId: undefined,
};

/**
 * Opens the new-guide dialog, seeded either empty or from the default equipment guide.
 *
 * The dialog itself is `FlowDetailsForm`, mounted once by `FlowInitializer` and driven by these two
 * atoms — so anything inside a `FlowInitializer` can raise it without rendering its own copy.
 */
export function useStartNewGuide(params: AppFlowParams) {
    const setEditingFlow = useSetAtom(editingFlowState);
    const setEditFlowOpen = useSetAtom(editFlowOpenState);
    const defaultFlow = useUnwrappedAtomValue(liveGuideFlowState(DEFAULT_EQUIPMENT_FLOW_PARAMS));

    // Only an equipment type can start from the default equipment guide, and not the default itself.
    const canUseDefault = params.spaceId !== DEFAULT_EQUIPMENT_SPACE_ID && !!params.equipmentTypeId && !!defaultFlow;

    const start = (nodes: AppFlowObject['nodes'], edges: AppFlowObject['edges'], nextId: number) => {
        setEditingFlow({
            object: 'app-flow',
            schema: 1,
            id: '',
            name: 'New Guide',
            space: params.spaceId,
            equipmentTypeId: params.equipmentTypeId,
            nodes,
            edges,
            nextId,
        });
        setEditFlowOpen(true);
    };

    return {
        canUseDefault,
        startEmpty: () => start([], [], 0),
        startFromDefault: () => defaultFlow && start(defaultFlow.nodes, defaultFlow.edges, defaultFlow.nextId),
    };
}
